// One-page landing at /. Pitch the product, three numbered value props,
// CTAs to sign up and sign in. Matches the editorial design system.
import { Link } from "react-router-dom";

export function LandingPage() {
  return (
    <>
      {/* Top nav */}
      <header className="flex items-center justify-between px-7 py-5">
        <Link
          to="/"
          className="font-display text-2xl tracking-[-0.04em] text-ink [font-variation-settings:'opsz'_96,'SOFT'_80,'WONK'_1,'wght'_500]"
        >
          Varo
        </Link>
        <nav className="flex items-center gap-6 text-sm">
          <Link to="/login" className="text-ink-soft hover:text-terra">
            Sign in
          </Link>
          <Link to="/signup" className="btn btn-primary text-xs">
            Claim your handle →
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden px-7 py-24 md:py-32">
        <div className="mx-auto max-w-4xl">
          <p className="eyebrow eyebrow-accent rise">A card without paper</p>
          <h1
            className="rise d1 mt-5 max-w-[18ch] text-6xl tracking-tight text-ink md:text-7xl lg:text-8xl"
            style={{
              fontVariationSettings: "'opsz' 144, 'SOFT' 30, 'WONK' 1, 'wght' 400",
              letterSpacing: "-0.045em",
              lineHeight: 0.92,
            }}
          >
            One link. <em className="not-italic italic text-terra">Hand it to anyone.</em>
          </h1>
          <p className="rise d2 mt-8 max-w-[52ch] text-lg leading-relaxed text-ink-soft">
            Varo is a digital business card. Three sections, one link, no app
            to install for the person you're handing it to. Update your role,
            photo, or links once — every share you've ever made updates.
          </p>
          <div className="rise d3 mt-9 flex flex-wrap gap-3">
            <Link to="/signup" className="btn btn-primary">
              Claim your handle →
            </Link>
            <Link to="/login" className="btn btn-secondary">
              Sign in
            </Link>
          </div>
          <p className="rise d4 mt-7 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-mute">
            Free · No credit card · Migrated from Mobilocard
          </p>
        </div>
      </section>

      <hr className="mx-auto max-w-4xl border-t border-rule" />

      {/* Three value props */}
      <section className="px-7 py-20 md:py-28">
        <div className="mx-auto max-w-4xl">
          <p className="eyebrow eyebrow-accent">How it works</p>
          <h2
            className="mt-4 max-w-[16ch] text-4xl tracking-tight md:text-5xl"
            style={{ fontVariationSettings: "'opsz' 96, 'SOFT' 30, 'wght' 400" }}
          >
            Three small steps. One shareable link.
          </h2>

          <div className="mt-14 grid gap-10 md:grid-cols-3">
            <ValueProp
              num="01"
              title="Identity"
              body="Pick a handle. The bare-path URL varo.biz/yourhandle is yours. Tell us your name, role, and how people reach you."
            />
            <ValueProp
              num="02"
              title="Card"
              body="Customize four sections — header, card, video, about — with the typography, colors, and backgrounds you want. Embed a YouTube video if it helps tell your story."
            />
            <ValueProp
              num="03"
              title="Share"
              body="Drop varo.biz/yourhandle in a chat, a bio, or an email signature. The link unfolds into a rich preview wherever you paste it."
            />
          </div>
        </div>
      </section>

      <hr className="mx-auto max-w-4xl border-t border-rule" />

      {/* Closing pitch */}
      <section className="px-7 py-24 md:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <h2
            className="text-4xl tracking-tight md:text-5xl"
            style={{ fontVariationSettings: "'opsz' 96, 'SOFT' 60, 'WONK' 1, 'wght' 400" }}
          >
            Your card,{" "}
            <em className="not-italic italic text-terra">always up to date.</em>
          </h2>
          <p className="mx-auto mt-7 max-w-[44ch] text-lg leading-relaxed text-ink-soft">
            Paper cards go out of date the day you print them. Varo cards
            update themselves the moment you change your role, your photo,
            or your phone number — across every link you've ever shared.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/signup" className="btn btn-primary">
              Claim your handle →
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-rule bg-paper-deep">
        <div className="mx-auto flex max-w-4xl flex-wrap items-baseline justify-between gap-4 px-7 py-9">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-mute">
            Set in Varo · 2026
          </p>
          <nav className="flex flex-wrap gap-5 text-sm text-ink-soft">
            <Link to="/terms" className="uline">
              Terms
            </Link>
            <Link to="/privacy" className="uline">
              Privacy
            </Link>
            <Link to="/login" className="uline">
              Sign in
            </Link>
            <a
              href="mailto:hello@varo.biz"
              className="uline"
            >
              hello@varo.biz
            </a>
          </nav>
        </div>
      </footer>
    </>
  );
}

function ValueProp({
  num,
  title,
  body,
}: {
  num: string;
  title: string;
  body: string;
}) {
  return (
    <div>
      <span className="font-display text-3xl italic leading-none text-terra [font-variation-settings:'opsz'_60,'wght'_300]">
        {num}
      </span>
      <h3 className="mt-3 text-2xl tracking-tight">{title}</h3>
      <p className="mt-3 leading-relaxed text-ink-soft">{body}</p>
    </div>
  );
}
